import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, Form, Input, Select, TimePicker, Typography, Image } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import { useScheduleUpsertModalB50 } from '../../../../context/ScheduleCreateModalContext';
import { getSelectedRouteRoutineFromSelectOption } from '../../../../utils/routeRoutine';
import { CleaningScheduleCard } from '../CleaningScheduleCard/CleaningScheduleCard';
import {
  combineScheduleDateAndTime,
  getDisabledPastScheduleTimes,
  isDisabledPastScheduleDate,
  isScheduleDateTimeInThePast,
} from '../../../../utils/schedule';
import { IconContainer, RobotNameText, RobotTypeText, ScheduleFormInfoFormItem } from './ScheduleFormInfo.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

export const ScheduleFormInfoB50 = (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const { isScheduleCreateFullSizeOpen, isReadOnly } = useMachineDetailsRobotSchedule();

  const {
    form,
    robot,
    scheduleRouteRoutine,
    scheduleRoutineOptions,
    scheduleRouteOptions,
    isLoadingScheduleRouteRoutine,
    hasErrorScheduleName,
    hasErrorScheduleRouteRoutine,
    handleScheduleRouteRoutineChange,
    handleEditScheduleRouteRoutine,
    handleValidateFields,
    isDropdownVisible,
    handleDropdownVisibleChange,
  } = useScheduleUpsertModalB50();

  const scheduleDate = Form.useWatch('scheduleDate', form);

  const routeRoutineOptions = useMemo(() => {
    const options = [];

    if (scheduleRoutineOptions.length > 0) {
      options.push({
        label: t('common.routines'),
        title: t('common.routines'),
        options: scheduleRoutineOptions.map(option => ({
          label: (
            <Flex align="center" gap={kccThemeConstants.SPACING.XS}>
              <SvgIcon name="routeRoutine" />
              <Typography.Text style={{ lineHeight: '20px' }}>{option.label}</Typography.Text>
            </Flex>
          ),
          searchLabel: option.label,
          value: option.value,
          routine: option.routine,
        })),
      });
    }

    if (scheduleRouteOptions.length > 0) {
      options.push({
        label: t('common.routes'),
        title: t('common.routes'),
        options: scheduleRouteOptions.map(option => ({
          label: (
            <Flex align="center" gap={kccThemeConstants.SPACING.XS}>
              <SvgIcon name="route" />
              <Typography.Text style={{ lineHeight: '20px' }}>{option.label}</Typography.Text>
            </Flex>
          ),
          searchLabel: option.label,
          value: option.value,
          route: option.route,
        })),
      });
    }

    return options;
  }, [t, scheduleRoutineOptions, scheduleRouteOptions]);

  const routeRoutineSelectSuffixIcon = useMemo(() => {
    if (isLoadingScheduleRouteRoutine) {
      return undefined;
    }

    if (isDropdownVisible) {
      return <SvgIcon name="search" />;
    }

    return <SvgIcon name="chevronDown" />;
  }, [isLoadingScheduleRouteRoutine, isDropdownVisible]);

  return (
    <Flex vertical gap={kccThemeConstants.SPACING.BASE}>
      {isScheduleCreateFullSizeOpen && (
        <>
          <Flex
            align="center"
            gap={kccThemeConstants.SPACING.SM}
            style={{ marginBottom: hasErrorScheduleName ? kccThemeConstants.SPACING.BASE : 0 }}
          >
            <IconContainer>
              <SvgIcon name="calendar" />
            </IconContainer>

            <ScheduleFormInfoFormItem
              name="scheduleName"
              rules={[{ required: true, message: t('createSchedule.scheduleNameRequired') }]}
            >
              <Input
                placeholder={t('createSchedule.scheduleNamePlaceholder')}
                onChange={(): void => handleValidateFields(['scheduleName'])}
              />
            </ScheduleFormInfoFormItem>
          </Flex>

          <Flex align="center" gap={kccThemeConstants.SPACING.SM}>
            <IconContainer>
              <SvgIcon name="robotNew" />
            </IconContainer>

            <ScheduleFormInfoFormItem>
              <Flex gap={kccThemeConstants.SPACING.SM} align="center" style={{ width: '100%' }}>
                <Image src={robot.variant?.picture.product} alt={robot.name} width={48} height={48} preview={false} />

                <Flex vertical gap={2}>
                  <RobotNameText>{robot.name}</RobotNameText>
                  <RobotTypeText>{robot.type?.name}</RobotTypeText>
                </Flex>
              </Flex>
            </ScheduleFormInfoFormItem>
          </Flex>
        </>
      )}

      <Flex
        align={scheduleRouteRoutine ? 'flex-start' : 'center'}
        gap={kccThemeConstants.SPACING.SM}
        style={{ marginBottom: hasErrorScheduleRouteRoutine ? kccThemeConstants.SPACING.BASE : 0 }}
      >
        <IconContainer>
          <SvgIcon name="routeRoutine" />
        </IconContainer>

        <ScheduleFormInfoFormItem
          name="scheduleRouteRoutine"
          rules={[{ required: true, message: t('createSchedule.routeRoutineRequired') }]}
        >
          {!scheduleRouteRoutine ? (
            <Select
              placeholder={t('createSchedule.routeRoutinePlaceholder')}
              allowClear={false}
              options={routeRoutineOptions}
              optionFilterProp="searchLabel"
              showSearch
              loading={isLoadingScheduleRouteRoutine}
              disabled={isLoadingScheduleRouteRoutine || isReadOnly}
              defaultOpen={isDropdownVisible && !isReadOnly}
              autoFocus={isDropdownVisible && !isReadOnly}
              open={isReadOnly ? false : isDropdownVisible}
              onDropdownVisibleChange={(visible): void => {
                if (isReadOnly) {
                  return;
                }

                handleDropdownVisibleChange(visible);
              }}
              suffixIcon={routeRoutineSelectSuffixIcon}
              onSelect={(_value, option): void => {
                if (isReadOnly) {
                  return;
                }

                const selected = getSelectedRouteRoutineFromSelectOption(option);
                if (selected) {
                  handleValidateFields(['scheduleRouteRoutine']);
                  handleScheduleRouteRoutineChange(selected);
                }
              }}
            />
          ) : (
            <CleaningScheduleCard
              isB50
              selectedSchedule={scheduleRouteRoutine}
              onEdit={handleEditScheduleRouteRoutine}
            />
          )}
        </ScheduleFormInfoFormItem>
      </Flex>

      <Flex align="center" gap={kccThemeConstants.SPACING.SM}>
        <IconContainer>
          <SvgIcon name="clock" />
        </IconContainer>

        <Flex gap={kccThemeConstants.SPACING.SM} style={{ width: '100%' }}>
          <ScheduleFormInfoFormItem
            name="scheduleDate"
            rules={[{ required: true, message: t('createSchedule.scheduleDateRequired') }]}
            style={{ flex: 1, margin: 0 }}
          >
            <DatePicker
              popupClassName="kcc-theme-date-picker-popup"
              allowClear={false}
              style={{ width: '100%', height: '40px' }}
              className="schedule-picker-active-border"
              suffixIcon={<SvgIcon name="calendar" />}
              disabled={isReadOnly}
              disabledDate={isDisabledPastScheduleDate}
              format={(date): string =>
                DateTime.formatDateByLocale(i18n.language, date.toISOString(), DATE_FORMAT_PATTERN.DATE)
              }
            />
          </ScheduleFormInfoFormItem>

          <ScheduleFormInfoFormItem
            name="scheduleTime"
            dependencies={['scheduleDate']}
            rules={[
              { required: true, message: t('createSchedule.scheduleTimeRequired') },
              {
                validator: (_, scheduleTime): Promise<void> => {
                  if (!scheduleDate || !scheduleTime) {
                    return Promise.resolve();
                  }

                  if (isScheduleDateTimeInThePast(combineScheduleDateAndTime(scheduleDate, scheduleTime))) {
                    return Promise.reject(new Error(t('createSchedule.scheduleMustBeInTheFuture')));
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{ flex: 1, margin: 0 }}
          >
            <TimePicker
              popupClassName="kcc-theme-time-picker-popup"
              format="HH:mm"
              allowClear={false}
              style={{ width: '100%', height: '40px' }}
              className="schedule-picker-active-border"
              suffixIcon={<SvgIcon name="clock" />}
              minuteStep={15}
              disabled={isReadOnly}
              disabledTime={getDisabledPastScheduleTimes(scheduleDate)}
            />
          </ScheduleFormInfoFormItem>
        </Flex>
      </Flex>
    </Flex>
  );
};
